import * as autobind from 'autobind';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'redux-scaffolding-ts';
import { Grid, Icon, Message } from 'semantic-ui-react';
import {
  NMFunctionalAreasStore,
  NMFunctionalAreaDto
} from 'stores/configuration/events-n-requests/non-machine-related/functional-areas-store';
import { ItemState, Query, OrderDefinition } from 'stores/dataStore';
import { CommandResult } from 'stores/types';
import { nameof } from 'utils/object';
import { TableModel, TableView } from 'widgets/collections/table';
import { TextBoxFilter } from 'widgets/collections/table-filters/textbox-filter';
import NewNMFunctionalAreaView from './new-nm-functional-area';
import ChangeNMFunctionalAreaView from './edit-nm-functional-area';
import { OptionsFilter } from 'widgets/collections/table-filters/option-filter';
import { Categories } from 'stores/requests/requests-store';

export interface NMFunctionalAreaListProps extends WithTranslation, RouteComponentProps {
  nmFunctionalAreas: NMFunctionalAreasStore;
}

export interface NMFunctionalAreaListState {
  query: Query;
  newNMFunctionalAreaShown: boolean;
  changeNMFunctionalAreaShown: boolean;
  activeFilters: string[];
  selectedItem: any;
  someFilterOpened: boolean;
}

@connect(['nmFunctionalAreas', NMFunctionalAreasStore])
class NMFunctionalAreaListPage extends React.Component<NMFunctionalAreaListProps, NMFunctionalAreaListState> {
  handleOnEnterKeydown = item => {
    this.setState({ selectedItem: item }, () => this.onEditItem());
  };

  handleOnRowDoubleClick = item => {
    this.handleOnEnterKeydown(item);
  };

  constructor(props) {
    super(props);
    this.state = {
      query: { searchQuery: '', orderBy: [{ direction: 'Descending', field: 'modifiedOn', useProfile: false }], skip: 0, take: 10 },
      newNMFunctionalAreaShown: false,
      changeNMFunctionalAreaShown: false,
      activeFilters: [],
      selectedItem: null,
      someFilterOpened: false
    };
  }

  componentDidMount() {
    this.load();
  }

  @autobind
  private load() {
    this.props.nmFunctionalAreas.getAllAsync(this.state.query);
  }

  @autobind
  private handleOrderBy(orderBy: OrderDefinition[]) {
    this.setState(
      {
        query: Object.assign(this.state.query, {
          orderBy: [...orderBy, { direction: 'Descending', field: 'modifiedOn', useProfile: false }]
        })
      },
      this.load
    );
  }

  @autobind
  private handlePageChange(skip: number, take: number) {
    this.setState({ query: Object.assign(this.state.query, { skip, take }) });
    this.load();
  }

  @autobind
  private async onSaveRow(item: NMFunctionalAreaDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.nmFunctionalAreas.saveAsync(item, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private async onDelete(item: NMFunctionalAreaDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.nmFunctionalAreas.deleteAsync(item.id, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private handleFilterChange(filters: { id: string; filter: any }[]) {
    const filter = filters.map(f => f.filter);
    const activeFilters = filters.map(f => f.id);

    const query = Object.assign(this.state.query, { filter, skip: 0 });
    this.setState({ query, activeFilters }, () => this.load());
  }

  @autobind
  private onNewItem() {
    this.setState({ newNMFunctionalAreaShown: true });
  }

  @autobind
  private onNewItemClosed(isSuccess: boolean) {
    this.setState({ newNMFunctionalAreaShown: false });
    if (isSuccess) this.load();
  }

  @autobind
  private onEditItem() {
    this.setState({ changeNMFunctionalAreaShown: true });
  }

  @autobind
  private onEditItemClosed(isSuccess: boolean) {
    this.setState({ changeNMFunctionalAreaShown: false });
    if (isSuccess) this.load();
  }

  handleOnActivateFilter = (visible: boolean) => {
    this.setState({ someFilterOpened: visible });
  };

  public render() {
    const { t } = this.props as any;
    const { activeFilters, someFilterOpened } = this.state;

    const tableModel = {
      columns: [
        {
          title: t('Name'),
          tooltipRenderer: true,
          newClassFromComponent: data => 'table__more-text-long',
          renderer: data => data.name,
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by name')}
              triggerTitle={title}
              onFilter={value =>
                onFilter(
                  nameof<NMFunctionalAreaDto>('name'),
                  `contains(tolower(${nameof<NMFunctionalAreaDto>('name')}), '${value.toLowerCase()}')`
                )
              }
              onClear={() => onClear(nameof<NMFunctionalAreaDto>('name'))}
              active={activeFilters.includes(nameof<NMFunctionalAreaDto>('name'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: nameof<NMFunctionalAreaDto>('name'),
            useProfile: false
          }
        },
        {
          title: t('Cluster'),
          tooltipRenderer: true,
          newClassFromComponent: data => 'table__more-text-long',
          renderer: data => data.clusterName,
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by cluster Name')}
              triggerTitle={title}
              onFilter={value => onFilter(nameof<NMFunctionalAreaDto>('clusterName'), `contains(Cluster/Name, '${value.toLowerCase()}')`)}
              onClear={() => onClear(nameof<NMFunctionalAreaDto>('clusterName'))}
              active={activeFilters.includes(nameof<NMFunctionalAreaDto>('clusterName'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: 'Cluster/Name',
            useProfile: false
          }
        },
        {
          title: t('Category'),
          tooltipRenderer: true,
          newClassFromComponent: data => 'table__more-text-long',
          renderer: data => (Categories[data.category] ? data.category : ''),
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <OptionsFilter
              filterTitle={t('Filter by category')}
              options={[
                { text: t('Technical'), value: 'Technical' },
                { text: t('Functional'), value: 'Functional' }
              ]}
              triggerTitle={title}
              onFilter={value => onFilter(nameof<NMFunctionalAreaDto>('category'), { Cluster: { Category: value } })}
              onClear={() => onClear(nameof<NMFunctionalAreaDto>('category'))}
              active={activeFilters.includes(nameof<NMFunctionalAreaDto>('category'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: { field: 'Cluster/Category', useProfile: false }
        }
      ],
      data: this.props.nmFunctionalAreas.state
    } as TableModel<NMFunctionalAreaDto>;
    return (
      <>
        <Grid className="event-types-list-grid">
          {this.props.nmFunctionalAreas.state.result && !this.props.nmFunctionalAreas.state.result.isSuccess && (
            <Grid.Row className="event-types-list-error-row">
              <Message
                className="error-message__style"
                icon="exclamation circle"
                error
                header={t('An error ocurred')}
                list={this.props.nmFunctionalAreas.state.result.messages.map(o => o.body)}
              />
            </Grid.Row>
          )}
          <Grid.Row className="event-types-list-items-row request-list__table-view">
            <TableView
              /////////////////For build table keyboard navegation/////////////////
              selectable={!this.state.newNMFunctionalAreaShown && !this.state.changeNMFunctionalAreaShown}
              maxSelection={1}
              onHideCheckbox={true}
              selectionType={'allRow'}
              onEnterKeydown={this.handleOnEnterKeydown}
              onRowDoubleClick={this.handleOnRowDoubleClick}
              preventEnterKeyDownEvent={someFilterOpened}
              //showActionsConfirmModal={true}
              /////////////////For build table keyboard navegation/////////////////
              model={tableModel}
              extraActions={[
                {
                  content: (
                    <>
                      <Icon name="edit" />
                      &nbsp;{t('Edit')}
                    </>
                  ),
                  onClick: item => {
                    this.setState({ selectedItem: item }, () => this.onEditItem());
                  }
                }
              ]}
              onOrderByChanged={this.handleOrderBy}
              onNewItem={this.onNewItem}
              onRefresh={this.load}
              canEdit={false}
              canDelete={true}
              onDeleteRow={this.onDelete}
              onSaveRow={this.onSaveRow}
              onPageChange={this.handlePageChange}
              onFilterChange={this.handleFilterChange}
              canCreateNew={true}
              createNewButtonTitle={t('Add Functional Area')}
            ></TableView>
          </Grid.Row>
        </Grid>
        {(this.state.newNMFunctionalAreaShown && <NewNMFunctionalAreaView onClose={this.onNewItemClosed} {...this.props} />) ||
          (this.state.changeNMFunctionalAreaShown && (
            <ChangeNMFunctionalAreaView onClose={this.onEditItemClosed} currentDto={this.state.selectedItem} />
          ))}
      </>
    );
  }
}

export default withTranslation()(NMFunctionalAreaListPage);
